import React from "react";
import { FaCheckCircle, FaTimesCircle, FaPhone, FaEnvelope, FaCalendarAlt, FaClock } from "react-icons/fa";

/**
 * Standardized Table Cell Components
 * Provides consistent typography and layout across all tables
 */

// Primary text with optional secondary text
export function TextCell({ primary, secondary, className = "" }) {
  return (
    <div className={`space-y-1 ${className}`}>
      <div className="font-medium text-gray-900">{primary || "-"}</div>
      {secondary && (
        <div className="text-sm text-gray-500">{secondary}</div>
      )}
    </div>
  );
}

// Contact information with icons
export function ContactCell({ phone, email, className = "" }) {
  return (
    <div className={`space-y-1 ${className}`}>
      {phone && (
        <div className="flex items-center gap-2">
          <FaPhone className="text-green-500 text-xs" />
          <span className="text-sm text-gray-900">{phone}</span>
        </div>
      )}
      {email && (
        <div className="flex items-center gap-2">
          <FaEnvelope className="text-blue-500 text-xs" />
          <span className="text-sm text-gray-600">{email}</span>
        </div>
      )}
    </div>
  );
}

// Verification status with icons
export function VerificationCell({ emailVerified, phoneVerified, className = "" }) {
  return (
    <div className={`space-y-1 ${className}`}>
      <div className="flex items-center gap-2">
        {emailVerified ? (
          <FaCheckCircle className="text-green-500 text-sm" />
        ) : (
          <FaTimesCircle className="text-red-500 text-sm" />
        )}
        <span className="text-xs text-gray-600">Email</span>
      </div>
      {phoneVerified !== undefined && (
        <div className="flex items-center gap-2">
          {phoneVerified ? (
            <FaCheckCircle className="text-green-500 text-sm" />
          ) : (
            <FaTimesCircle className="text-red-500 text-sm" />
          )}
          <span className="text-xs text-gray-600">Phone</span>
        </div>
      )}
    </div>
  );
}

// Date information with icons
export function DateCell({ created, updated, className = "" }) {
  return (
    <div className={`space-y-1 ${className}`}>
      {created && (
        <div className="flex items-center gap-2">
          <FaCalendarAlt className="text-blue-500 text-xs" />
          <span className="text-sm text-gray-900">
            {new Date(created).toLocaleDateString()}
          </span>
        </div>
      )}
      {updated && (
        <div className="flex items-center gap-2">
          <FaClock className="text-green-500 text-xs" />
          <span className="text-xs text-gray-500">
            Updated: {new Date(updated).toLocaleDateString()}
          </span>
        </div>
      )}
    </div>
  );
}

// User avatar with name and email
export function UserCell({ name, email, avatar, className = "" }) {
  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {avatar ? (
        <img 
          src={avatar} 
          alt={name} 
          className="w-10 h-10 rounded-full object-cover"
        />
      ) : (
        <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
          {getInitials(name)}
        </div>
      )}
      <div>
        <div className="font-medium text-gray-900">{name || "Unknown"}</div>
        <div className="text-sm text-gray-500">{email || "-"}</div>
      </div>
    </div>
  );
}

// Simple text cell
export function SimpleCell({ value, className = "" }) {
  return (
    <div className={`text-sm text-gray-900 ${className}`}>
      {value || "-"}
    </div>
  );
}

// Number cell with optional formatting
export function NumberCell({ value, format = "number", currency = "MMK", className = "" }) {
  const formatValue = (val) => {
    if (format === "currency") {
      return `${Number(val).toLocaleString()} ${currency}`;
    }
    if (format === "number") {
      return Number(val).toLocaleString();
    }
    return val;
  };

  return (
    <div className={`text-sm font-medium text-gray-900 ${className}`}>
      {value ? formatValue(value) : "-"}
    </div>
  );
}
